import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAdmin } from '@/hooks/useAdmin';
import { Save, ArrowLeft, Loader2, Eye, FileEdit, Languages } from 'lucide-react';
import { toast } from 'sonner';
import RichTextEditor from '@/components/RichTextEditor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_HOST = process.env.REACT_APP_BACKEND_URL;
const LANGS = ['hr', 'en', 'de', 'es', 'fr', 'pt', 'zh', 'ar', 'hi', 'ru', 'ja'];

export default function StaticPageEdit() {
  const { slug } = useParams();
  const { getAccessTokenSilently } = useAdmin();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [retranslating, setRetranslating] = useState(false);
  const [lang, setLang] = useState('hr');
  const [tab, setTab] = useState('edit');

  // For pages with rich presentational HTML (Impressum, O-nama) default to source
  // so that TipTap does not strip custom blocks on first render.
  useEffect(() => {
    if (slug === 'impressum' || slug === 'o-nama') setTab('source');
  }, [slug]);

  const load = useCallback(async () => {
    const token = await getAccessTokenSilently();
    const r = await fetch(`${API}/admin/static-pages/${slug}`, { headers: { Authorization: `Bearer ${token}` } });
    if (r.ok) setPage((await r.json()).page);
    setLoading(false);
  }, [slug, getAccessTokenSilently]);

  useEffect(() => { load(); }, [load]);

  async function save(reTranslate = false) {
    setSaving(true);
    try {
      const token = await getAccessTokenSilently();
      const r = await fetch(`${API}/admin/static-pages/${slug}`, {
        method: 'PUT', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title_hr: page.title_hr,
          body_hr: page.body_hr,
          meta_description: page.meta_description,
          title_translations: page.title_translations,
          body_translations: page.body_translations,
          meta_translations: page.meta_translations,
        }),
      });
      if (r.ok) {
        if (reTranslate) {
          setRetranslating(true);
          await fetch(`${API}/admin/static-pages/${slug}/retranslate`, {
            method: 'POST', headers: { Authorization: `Bearer ${token}` }
          });
          setRetranslating(false);
          await load();
          toast.success('Spremljeno + svi jezici prevedeni');
        } else toast.success('Spremljeno');
      } else toast.error('Greška');
    } finally { setSaving(false); }
  }

  if (loading || !page) return <div className="p-10 flex items-center justify-center"><Loader2 className="animate-spin text-zinc-500" /></div>;

  const isHR = lang === 'hr';
  const title = isHR ? page.title_hr : (page.title_translations?.[lang] || '');
  const body = isHR ? page.body_hr : (page.body_translations?.[lang] || '');
  const updateTitle = (v) => isHR
    ? setPage(p => ({ ...p, title_hr: v }))
    : setPage(p => ({ ...p, title_translations: { ...(p.title_translations || {}), [lang]: v } }));
  const updateBody = (v) => isHR
    ? setPage(p => ({ ...p, body_hr: v }))
    : setPage(p => ({ ...p, body_translations: { ...(p.body_translations || {}), [lang]: v } }));

  const previewHtml = (body || '').replace(/src="\/api\//g, `src="${BACKEND_HOST}/api/`).replace(/href="\/api\//g, `href="${BACKEND_HOST}/api/`)
    // Visualize the embedded imprint-tree widget marker in the preview
    .replace(
      /<div\b[^>]*\bem-imprint-tree\b[^>]*>[\s\S]*?<\/div>/gi,
      '<div style="border:2px dashed #ef4444;border-radius:8px;padding:14px 18px;text-align:center;background:rgba(239,68,68,.08);color:#b91c1c;font-size:11px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;margin:1em 0">◆ Widget tima / Impressum (renderira se javno) ◆</div>'
    );

  return (
    <div className="p-6 max-w-5xl" data-testid="static-page-edit">
      <Link to="/admin/static-pages" className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-white mb-4"><ArrowLeft size={12} /> Natrag</Link>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-white">Uredi: {page.slug}</h1>
        <div className="flex gap-2">
          <button onClick={() => save(false)} disabled={saving} className="flex items-center gap-2 px-4 py-2 bg-zinc-700 hover:bg-zinc-600 text-white text-sm font-bold rounded disabled:opacity-50" data-testid="static-save-btn">
            <Save size={14} /> {saving ? 'Spremam...' : 'Spremi draft'}
          </button>
          <button onClick={() => save(true)} disabled={saving || retranslating} className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-bold rounded disabled:opacity-50" data-testid="static-save-translate-btn">
            <Languages size={14} /> {retranslating ? 'Prevodim...' : 'Spremi i prevedi'}
          </button>
        </div>
      </div>

      {/* Language tabs */}
      <div className="flex flex-wrap gap-1 mb-4 border-b border-zinc-800 pb-3">
        {LANGS.map(l => (
          <button key={l} onClick={() => setLang(l)}
                  className={`px-3 py-1.5 text-xs uppercase rounded ${lang === l ? 'bg-zinc-700 text-white font-bold' : 'text-zinc-400 hover:bg-zinc-800'}`}
                  data-testid={`static-lang-${l}`}>{l}</button>
        ))}
      </div>

      {/* Edit / Source / Preview tabs */}
      <div className="flex gap-1 mb-4">
        <button onClick={() => setTab('edit')} className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded ${tab === 'edit' ? 'bg-zinc-700 text-white' : 'text-zinc-400 hover:bg-zinc-800'}`} data-testid="tab-edit">
          <FileEdit size={12} /> Vizualni urednik
        </button>
        <button onClick={() => setTab('source')} className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded ${tab === 'source' ? 'bg-zinc-700 text-white' : 'text-zinc-400 hover:bg-zinc-800'}`} data-testid="tab-source">
          <span className="font-mono text-[11px] font-bold px-0.5">&lt;/&gt;</span> HTML izvor
        </button>
        <button onClick={() => setTab('preview')} className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded ${tab === 'preview' ? 'bg-zinc-700 text-white' : 'text-zinc-400 hover:bg-zinc-800'}`} data-testid="tab-preview">
          <Eye size={12} /> Pregled
        </button>
      </div>

      {tab === 'edit' ? (
        <>
          <label className="block text-xs text-zinc-400 mb-1">Naslov ({lang})</label>
          <input value={title} onChange={e => updateTitle(e.target.value)} className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded text-white text-sm mb-4" data-testid="static-title-input" />

          <div className="mb-2 p-2 bg-amber-900/20 border border-amber-700/50 rounded text-[11px] text-amber-300" data-testid="visual-editor-warning">
            <strong>⚠️ Napomena:</strong> Vizualni urednik podržava jednostavne elemente (tekst, naslove, liste, slike). Ako stranica sadrži napredne HTML blokove (hero, kartice, kolone s class atributima), koristi tab <strong>HTML izvor</strong> — vizualni urednik bi inače uklonio te stilove.
          </div>

          <label className="block text-xs text-zinc-400 mb-1">Sadržaj ({lang})</label>
          <RichTextEditor
            initialContent={body}
            onChange={updateBody}
            enablePdf
            enableColumns
            enableImprintWidget={page.slug === 'impressum' || page.slug === 'o-nama'}
            getAuthToken={getAccessTokenSilently}
          />
          {isHR && (
            <>
              <label className="block text-xs text-zinc-400 mt-4 mb-1">Meta description</label>
              <input value={page.meta_description || ''} onChange={e => setPage(p => ({ ...p, meta_description: e.target.value }))} className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded text-white text-sm" />
            </>
          )}
        </>
      ) : tab === 'source' ? (
        <>
          <label className="block text-xs text-zinc-400 mb-1">Naslov ({lang})</label>
          <input value={title} onChange={e => updateTitle(e.target.value)} className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded text-white text-sm mb-4" data-testid="static-source-title-input" />

          <div className="mb-2 p-2 bg-emerald-900/20 border border-emerald-700/50 rounded text-[11px] text-emerald-300" data-testid="source-editor-info">
            <strong>HTML izvor:</strong> Ovdje možeš urediti bilo koji dio stranice – tekstove, naslove, klase, karticе, herove. Sve izmjene se čuvaju točno kako ih napišeš. Za widget tima dodaj: <code className="bg-black/30 px-1 rounded">&lt;div class="em-imprint-tree" data-widget="imprint-tree"&gt;&lt;/div&gt;</code>
          </div>

          <label className="block text-xs text-zinc-400 mb-1">HTML sadržaj ({lang})</label>
          <textarea
            value={body}
            onChange={e => updateBody(e.target.value)}
            spellCheck={false}
            className="w-full min-h-[500px] px-3 py-2 bg-zinc-900 border border-zinc-700 rounded text-zinc-100 text-xs font-mono leading-relaxed"
            data-testid="static-source-textarea"
          />
          {isHR && (
            <>
              <label className="block text-xs text-zinc-400 mt-4 mb-1">Meta description</label>
              <input value={page.meta_description || ''} onChange={e => setPage(p => ({ ...p, meta_description: e.target.value }))} className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded text-white text-sm" data-testid="static-source-meta-input" />
            </>
          )}
        </>
      ) : (
        <div className="bg-white dark:bg-[#0b1220] rounded-lg p-8 max-w-3xl border border-zinc-200 text-gray-900 dark:text-gray-100" data-testid="preview-pane">
          <h1 className="text-3xl font-bold mb-6">{title}</h1>
          <div className="prose prose-lg max-w-none [&_.em-columns]:grid [&_.em-columns]:gap-6 [&_.em-columns[data-cols='2']]:grid-cols-2 [&_.em-columns[data-cols='3']]:grid-cols-3 [&_.em-columns[data-cols='4']]:grid-cols-4 [&_.pdf-attachment]:inline-flex [&_.pdf-attachment]:items-center [&_.pdf-attachment]:gap-2 [&_.pdf-attachment]:px-4 [&_.pdf-attachment]:py-2 [&_.pdf-attachment]:bg-red-50 [&_.pdf-attachment]:text-red-700 [&_.pdf-attachment]:rounded [&_.pdf-attachment]:no-underline" dangerouslySetInnerHTML={{ __html: previewHtml }} />
        </div>
      )}
    </div>
  );
}
